import { useFormContext, useFormState } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Coins, Gift, Flame, HelpCircle } from "lucide-react";
import { Dog } from "@/components/icons/Dog";
import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Rocket } from "lucide-react";

// Default values for the form
const defaultFormValues = {
  totalSupply: "",
  totalSupplyPreset: "custom",
  name: "",
  symbol: "",
  decimals: "18",
  buyTax: "0",
  sellTax: "0",
  transferTax: "0",
  taxDistribution: {
    reflection: "0",
    autoLiquidity: "0",
    burn: "0",
    marketing: "0",
    development: "0",
    marketingWallets: [],
    developmentWallets: []
  },
  hasTax: false,
  hasReflection: false,
  hasAutoLiquidity: false,
  hasBurn: false,
  hasMarketing: false,
  hasDevelopment: false
};

const templates = {
  default: {
    name: "Basic (Customizable)",
    icon: Coins,
    description: "Start with a clean slate and customize all settings to your needs. Perfect for experienced users who want full control over their token configuration.",
    values: {
      ...defaultFormValues,
      totalSupply: "",
      totalSupplyPreset: "custom"
    }
  },
  memeCoin: {
    name: "Meme Coin (Customizable)",
    icon: Dog,
    description: "Pre-configured for meme tokens with a 1 trillion initial supply (like SHIB). All settings can be customized after selection. Ideal for community-driven tokens and social projects.",
    values: {
      ...defaultFormValues,
      totalSupply: "1000000000000",
      totalSupplyPreset: "1000000000000",
      hasTax: false,
      buyTax: "0",
      sellTax: "0",
      transferTax: "0",
      hasReflection: false,
      hasAutoLiquidity: false,
      hasBurn: false,
      hasMarketing: false,
      hasDevelopment: false,
      taxDistribution: {
        reflection: "0",
        autoLiquidity: "0",
        burn: "0",
        marketing: "0",
        development: "0",
        marketingWallets: [],
        developmentWallets: []
      }
    }
  },
  reflectionDividends: {
    name: "Reflections/Dividends (Customizable)",
    icon: Gift,
    description: "Pre-configured for reflection rewards with 1 billion supply. Features 2% buy tax and 5% sell tax with 100% reflection distribution. Perfect for dividend-focused tokens.",
    values: {
      ...defaultFormValues,
      totalSupply: "1000000000",
      totalSupplyPreset: "1000000000",
      hasTax: true,
      buyTax: "2",
      sellTax: "5",
      transferTax: "0",
      hasReflection: true,
      hasAutoLiquidity: false,
      hasBurn: false,
      hasMarketing: false,
      hasDevelopment: false,
      taxDistribution: {
        reflection: "100",
        autoLiquidity: "0",
        burn: "0",
        marketing: "0",
        development: "0",
        marketingWallets: [],
        developmentWallets: []
      }
    }
  },
  autoLiquidity: {
    name: "Auto-Liquidity (Customizable)",
    icon: Coins,
    description: "Pre-configured for automatic liquidity generation with 1 million supply. Features 1% buy and sell tax with 100% auto-liquidity distribution. Perfect for maintaining stable price floors.",
    values: {
      ...defaultFormValues,
      totalSupply: "1000000",
      totalSupplyPreset: "1000000",
      hasTax: true,
      buyTax: "1",
      sellTax: "1",
      transferTax: "0",
      hasReflection: false,
      hasAutoLiquidity: true,
      hasBurn: false,
      hasMarketing: false,
      hasDevelopment: false,
      taxDistribution: {
        reflection: "0",
        autoLiquidity: "100",
        burn: "0",
        marketing: "0",
        development: "0",
        marketingWallets: [],
        developmentWallets: []
      }
    }
  },
  autoBurn: {
    name: "Auto-Burn (Customizable)",
    icon: Flame,
    description: "Pre-configured for automatic token burning with 1 trillion supply. Features 1% buy and sell tax with 100% burn distribution. Perfect for deflationary tokens.",
    values: {
      ...defaultFormValues,
      totalSupply: "1000000000000",
      totalSupplyPreset: "1000000000000",
      hasTax: true,
      buyTax: "1",
      sellTax: "1",
      transferTax: "0",
      hasReflection: false,
      hasAutoLiquidity: false,
      hasBurn: true,
      hasMarketing: false,
      hasDevelopment: false,
      taxDistribution: {
        reflection: "0",
        autoLiquidity: "0",
        burn: "100",
        marketing: "0",
        development: "0",
        marketingWallets: [],
        developmentWallets: []
      }
    }
  }
};

export function TemplateSelect() {
  const { setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-lg border bg-card text-card-foreground" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}>
      <div className="flex items-center justify-between p-6">
        <div>
          <h3 className="text-lg font-semibold">Choose a Token Template</h3>
          <p className="text-sm text-muted-foreground">Select a template to get started with your token</p>
        </div>
        <span
          className={`transform transition-transform duration-200 cursor-pointer ${isOpen ? 'rotate-180' : ''}`}
          style={{
            color: `var(--color-secondary, #10b981)`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          ▼
        </span>
      </div>
      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-6 pt-0">
          <div className="grid gap-4">
            {/* Basic template - full width */}
            <button
              key="default"
              className="flex flex-col items-start p-4 rounded-lg border hover:bg-accent/5 transition-colors duration-200"
              style={{ borderColor: 'color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)' }}
              onClick={() => {
                Object.entries(templates.default.values).forEach(([field, value]) => {
                  setValue(field, value);
                });
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Coins className="w-5 h-5" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
                <span className="font-medium">{templates.default.name}</span>
              </div>
              <p className="text-sm text-muted-foreground text-left">
                {templates.default.description}
              </p>
            </button>

            {/* Other templates - grid layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(templates)
                .filter(([key]) => key !== 'default')
                .map(([key, template]) => (
                  <button
                    key={key}
                    className="flex flex-col items-start p-4 rounded-lg border hover:bg-accent/5 transition-colors duration-200"
                    style={{ borderColor: 'color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)' }}
                    onClick={() => {
                      Object.entries(template.values).forEach(([field, value]) => {
                        setValue(field, value);
                      });
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {template.icon && <template.icon className="w-5 h-5" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />}
                      <span className="font-medium">{template.name}</span>
                    </div>
                    <p className="text-sm text-muted-foreground text-left">
                      {template.description}
                    </p>
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
